import React, { useEffect, useState } from 'react';
import { useBartenders, useCreateBartender, useDeleteBartender, useGenerateBartenderTap, useUpdateBartender } from '../hooks/useEntraUsers';
import { useSession } from '../lib/session';
import { useToast } from '../lib/toast';
import { Icon } from '../components/Icon';
import type { EntraUser, EntraUserCreateResponse } from '../api/types';
import type { CreateBartenderBody } from '../api/client';
import '../styles/bartenders.css';

// ---------------------------------------------------------------------------
// TAP result (shown after successful creation)
// ---------------------------------------------------------------------------

function TapResult({ result, onClose }: { result: EntraUserCreateResponse; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const tap = result.temporaryAccessPass;
  const displayName =
    (result.user.displayName ??
      [result.user.givenName, result.user.surname].filter(Boolean).join(' ')) ||
    result.user.userPrincipalName;

  const handleCopy = () => {
    void navigator.clipboard.writeText(tap.temporaryAccessPasscode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div>
      <div className="bartenders-tap-created">
        <Icon name="check" size={16} />
        <span className="bartenders-tap-created-name">{displayName} created</span>
      </div>
      <div className="bartenders-tap-upn">
        UPN: <span className="bartenders-tap-upn-value">{result.user.userPrincipalName}</span>
      </div>

      <div className="bartenders-tap-box">
        <div className="uppercase-label bartenders-tap-label">Temporary Access Pass</div>
        <div className="bartenders-tap-row">
          <code className="bartenders-tap-code">{tap.temporaryAccessPasscode}</code>
          <button
            onClick={handleCopy}
            className={`bartenders-copy-btn${copied ? ' bartenders-copy-btn--copied' : ''}`}
          >
            <Icon name={copied ? 'check' : 'notes'} size={14} />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <p className="bartenders-tap-note">
          Valid for {tap.lifetimeInMinutes} minutes.{' '}
          <strong>This code cannot be retrieved again.</strong>{' '}
          Share it with {result.user.givenName ?? 'the new employee'} now.
        </p>
      </div>

      <div className="bartenders-tap-done-row">
        <button onClick={onClose} className="bartenders-btn-primary">Done</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create bartender modal
// ---------------------------------------------------------------------------

function CreateBartenderModal({ onClose }: { onClose: () => void }) {
  const { toast } = useToast();
  const createMutation = useCreateBartender();
  const [result, setResult] = useState<EntraUserCreateResponse | null>(null);

  const [form, setForm] = useState({
    givenName: '', surname: '', mobilePhone: '', personalEmail: '', hireDate: '',
  });
  const [groups, setGroups] = useState({ Manchester: false, 'Public House': false, Cornerstone: false });

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const toggleGroup = (name: keyof typeof groups) =>
    setGroups((prev) => ({ ...prev, [name]: !prev[name] }));

  const upnPreview =
    form.givenName && form.surname
      ? `${form.givenName.charAt(0).toLowerCase()}${form.surname.toLowerCase().replace(/[^a-z0-9]/g, '')}@etherealbrew.com`
      : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedGroups = (Object.keys(groups) as Array<keyof typeof groups>).filter((g) => groups[g]);
    const body: CreateBartenderBody = {
      givenName: form.givenName.trim(),
      surname: form.surname.trim(),
      personalEmail: form.personalEmail.trim(),
      hireDate: form.hireDate,
      mobilePhone: form.mobilePhone.trim() || null,
      groups: selectedGroups,
    };
    createMutation.mutate(body, {
      onSuccess: (data) => setResult(data),
      onError: (err) => toast.error((err as Error).message ?? 'Failed to create bartender.'),
    });
  };

  return (
    <div
      className="bartenders-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !result) onClose(); }}
    >
      <div className="bartenders-modal">
        <div className="bartenders-modal-header">
          <div>
            <div className="uppercase-label bartenders-modal-label">Bartenders</div>
            <h2 className="bartenders-modal-title">
              {result ? 'Account created' : 'New bartender'}
            </h2>
          </div>
          {!result && (
            <button onClick={onClose} className="bartenders-modal-close" aria-label="Close">
              <Icon name="close" size={20} />
            </button>
          )}
        </div>

        {result ? (
          <TapResult result={result} onClose={() => { setResult(null); onClose(); }} />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="bartenders-form">
              <div className="form-field">
                <label htmlFor="bf-givenName">First name *</label>
                <input id="bf-givenName" value={form.givenName} onChange={set('givenName')} required autoFocus />
              </div>
              <div className="form-field">
                <label htmlFor="bf-surname">Last name *</label>
                <input id="bf-surname" value={form.surname} onChange={set('surname')} required />
              </div>
              <div className="form-field">
                <label htmlFor="bf-personalEmail">Personal email *</label>
                <input id="bf-personalEmail" type="email" value={form.personalEmail} onChange={set('personalEmail')} required />
              </div>
              <div className="form-field">
                <label htmlFor="bf-hireDate">Hire date *</label>
                <input id="bf-hireDate" type="date" value={form.hireDate} onChange={set('hireDate')} required />
              </div>
              <div className="form-field">
                <label htmlFor="bf-mobilePhone">Mobile phone</label>
                <input id="bf-mobilePhone" type="tel" value={form.mobilePhone} onChange={set('mobilePhone')} placeholder="859-123-4567" />
              </div>

              <div className="form-field">
                <label>Add to groups</label>
                <div className="bartenders-group-checkboxes">
                  {(Object.keys(groups) as Array<keyof typeof groups>).map((name) => (
                    <label key={name} className="bartenders-group-checkbox-label">
                      <input
                        type="checkbox"
                        checked={groups[name]}
                        onChange={() => toggleGroup(name)}
                      />
                      {name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="bartenders-upn-preview">
                {upnPreview ? (
                  <>
                    Username:{' '}
                    <span className="bartenders-upn-preview-value">{upnPreview}</span>
                    {' '}· M365 F1 license assigned · Personal email set as MFA method
                  </>
                ) : (
                  'Enter first and last name to preview the generated username.'
                )}
              </div>
            </div>

            <div className="bartenders-form-actions">
              <button type="button" className="bartenders-btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="bartenders-btn-submit" disabled={createMutation.isPending}>
                {createMutation.isPending
                  ? <><Icon name="clock" size={14} /> Creating…</>
                  : <><Icon name="plus" size={14} /> Create bartender</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit bartender modal
// ---------------------------------------------------------------------------

function EditBartenderModal({ user, onClose }: { user: EntraUser; onClose: () => void }) {
  const { toast } = useToast();
  const updateMutation = useUpdateBartender();

  const hireDateValue = user.employeeHireDate
    ? user.employeeHireDate.slice(0, 10)
    : '';

  const [form, setForm] = useState({
    givenName: user.givenName ?? '',
    surname: user.surname ?? '',
    displayName: user.displayName ?? '',
    personalEmail: user.otherMails[0] ?? '',
    mobilePhone: user.mobilePhone ?? '',
    hireDate: hireDateValue,
  });

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(
      {
        userId: user.id,
        body: {
          givenName: form.givenName.trim() || undefined,
          surname: form.surname.trim() || undefined,
          displayName: form.displayName.trim() || undefined,
          mobilePhone: form.mobilePhone.trim() || null,
          otherMails: form.personalEmail.trim() ? [form.personalEmail.trim()] : undefined,
          hireDate: form.hireDate || null,
        },
      },
      {
        onSuccess: () => {
          toast.success('Bartender updated.');
          onClose();
        },
        onError: (err) => toast.error((err as Error).message ?? 'Failed to update bartender.'),
      },
    );
  };

  return (
    <div
      className="bartenders-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bartenders-modal">
        <div className="bartenders-modal-header">
          <div>
            <div className="uppercase-label bartenders-modal-label">Bartenders</div>
            <h2 className="bartenders-modal-title">Edit bartender</h2>
          </div>
          <button onClick={onClose} className="bartenders-modal-close" aria-label="Close">
            <Icon name="close" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bartenders-form">
            <div className="form-field">
              <label htmlFor="be-givenName">First name</label>
              <input id="be-givenName" value={form.givenName} onChange={set('givenName')} autoFocus />
            </div>
            <div className="form-field">
              <label htmlFor="be-surname">Last name</label>
              <input id="be-surname" value={form.surname} onChange={set('surname')} />
            </div>
            <div className="form-field">
              <label htmlFor="be-displayName">Display name</label>
              <input id="be-displayName" value={form.displayName} onChange={set('displayName')} />
            </div>
            <div className="form-field">
              <label htmlFor="be-personalEmail">Personal email</label>
              <input id="be-personalEmail" type="email" value={form.personalEmail} onChange={set('personalEmail')} />
            </div>
            <div className="form-field">
              <label htmlFor="be-mobilePhone">Phone number</label>
              <input id="be-mobilePhone" type="tel" value={form.mobilePhone} onChange={set('mobilePhone')} placeholder="859-123-4567" />
            </div>
            <div className="form-field">
              <label htmlFor="be-hireDate">Hire date</label>
              <input id="be-hireDate" type="date" value={form.hireDate} onChange={set('hireDate')} />
            </div>
          </div>

          <div className="bartenders-form-actions">
            <button type="button" className="bartenders-btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="bartenders-btn-submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending
                ? <><Icon name="clock" size={14} /> Saving…</>
                : <><Icon name="check" size={14} /> Save changes</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Generate TAP modal (for existing bartenders)
// ---------------------------------------------------------------------------

function GenerateTapModal({ userId, displayName, onClose }: {
  userId: string;
  displayName: string;
  onClose: () => void;
}): React.ReactElement {
  const tapMutation = useGenerateBartenderTap();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    tapMutation.mutate(userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tap = tapMutation.data?.temporaryAccessPass;

  const handleCopy = () => {
    if (!tap) return;
    void navigator.clipboard.writeText(tap.temporaryAccessPasscode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div
      className="bartenders-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !tapMutation.isPending) onClose(); }}
    >
      <div className="bartenders-modal">
        <div className="bartenders-modal-header">
          <div>
            <div className="uppercase-label bartenders-modal-label">Bartenders</div>
            <h2 className="bartenders-modal-title">Temporary Access Pass</h2>
          </div>
          {!tapMutation.isPending && (
            <button onClick={onClose} className="bartenders-modal-close" aria-label="Close">
              <Icon name="close" size={20} />
            </button>
          )}
        </div>

        {tapMutation.isPending && (
          <div className="bartenders-loading">
            <div className="spinner bartenders-spinner" />
            Generating passcode for {displayName}…
          </div>
        )}

        {tapMutation.isError && (
          <div>
            <p className="bartenders-tap-note" style={{ color: 'var(--text-muted)', marginBottom: '16px' }}>
              Failed to generate a passcode. Please try again.
            </p>
            <div className="bartenders-form-actions">
              <button className="bartenders-btn-ghost" onClick={onClose}>Close</button>
              <button className="bartenders-btn-submit" onClick={() => tapMutation.mutate(userId)}>
                Retry
              </button>
            </div>
          </div>
        )}

        {tap && (
          <div>
            <div className="bartenders-tap-upn">
              For: <span className="bartenders-tap-upn-value">{displayName}</span>
            </div>
            <div className="bartenders-tap-box">
              <div className="uppercase-label bartenders-tap-label">Temporary Access Pass</div>
              <div className="bartenders-tap-row">
                <code className="bartenders-tap-code">{tap.temporaryAccessPasscode}</code>
                <button
                  onClick={handleCopy}
                  className={`bartenders-copy-btn${copied ? ' bartenders-copy-btn--copied' : ''}`}
                >
                  <Icon name={copied ? 'check' : 'notes'} size={14} />
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="bartenders-tap-note">
                Valid for {tap.lifetimeInMinutes} minutes.{' '}
                <strong>This code cannot be retrieved again.</strong>{' '}
                Share it with {displayName} now.
              </p>
            </div>
            <div className="bartenders-tap-done-row">
              <button onClick={onClose} className="bartenders-btn-primary">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function BartendersPage(): React.ReactElement {
  const { hasRole } = useSession();
  const { toast } = useToast();
  const { data, isLoading } = useBartenders();
  const deleteMutation = useDeleteBartender();
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [tapModalUser, setTapModalUser] = useState<{ id: string; displayName: string } | null>(null);
  const [editUser, setEditUser] = useState<EntraUser | null>(null);

  if (!hasRole('Admin', 'BarManager')) {
    return (
      <div className="bartenders-no-access">
        <div className="bartenders-no-access-alert">
          <Icon name="alert" size={18} />
          <span>You don't have permission to view this page.</span>
        </div>
      </div>
    );
  }

  const bartenders = data?.items ?? [];

  const filtered = search
    ? bartenders.filter((u) => {
        const name = `${u.givenName ?? ''} ${u.surname ?? ''}`.toLowerCase();
        return (
          name.includes(search.toLowerCase()) ||
          u.userPrincipalName.toLowerCase().includes(search.toLowerCase()) ||
          (u.mobilePhone ?? '').includes(search)
        );
      })
    : bartenders;

  const handleDelete = (userId: string) => {
    deleteMutation.mutate(userId, {
      onSuccess: () => {
        toast.success('Bartender removed from Entra.');
        setConfirmDeleteId(null);
      },
      onError: () => {
        toast.error('Failed to delete. Check API logs.');
        setConfirmDeleteId(null);
      },
    });
  };

  return (
    <div className="bartenders-page">

      <div className="bartenders-page-header">
        <div className="uppercase-label">People</div>
        <div className="bartenders-title-row">
          <div>
            <h1 className="bartenders-title">Bartenders</h1>
            <p className="bartenders-meta">
              {bartenders.length > 0
                ? `${bartenders.length} active Entra account${bartenders.length === 1 ? '' : 's'} with job title Bartender.`
                : 'Active Entra accounts with job title Bartender.'}
            </p>
          </div>
          <button className="bartenders-btn-add" onClick={() => setModalOpen(true)}>
            <Icon name="plus" size={15} /> New bartender
          </button>
        </div>
      </div>

      <div className="bartenders-search-wrap">
        <input
          className="bartenders-search"
          type="search"
          placeholder="Filter by name, UPN, or phone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="bartenders-table-container">
        {isLoading ? (
          <div className="bartenders-loading">
            <div className="spinner bartenders-spinner" />
            Loading bartenders…
          </div>
        ) : (
          <table className="bartenders-table">
            <thead>
              <tr>
                <th className="bartenders-th">NAME</th>
                <th className="bartenders-th">UPN</th>
                <th className="bartenders-th">MOBILE</th>
                <th className="bartenders-th">PERSONAL EMAIL</th>
                <th className="bartenders-th">HIRE DATE</th>
                <th className="bartenders-th bartenders-th--right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="bartenders-td bartenders-empty-td">
                    {search ? 'No bartenders match that filter.' : 'No bartenders found.'}
                  </td>
                </tr>
              ) : (
                filtered.map((user) => {
                  const hireDate = user.employeeHireDate
                    ? new Date(user.employeeHireDate).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
                    : null;
                  const displayName =
                    (user.displayName ??
                      [user.givenName, user.surname].filter(Boolean).join(' ')) ||
                    user.userPrincipalName;
                  const personalEmail = user.otherMails[0] ?? null;
                  const isConfirming = confirmDeleteId === user.id;

                  return (
                    <tr key={user.id}>
                      <td
                        className="bartenders-td bartenders-td--name bartenders-td--clickable"
                        onClick={() => setEditUser(user)}
                      >
                        {displayName}
                      </td>
                      <td className="bartenders-td bartenders-td--mono">
                        {user.userPrincipalName}
                      </td>
                      <td className={`bartenders-td ${user.mobilePhone ? 'bartenders-td--mono' : 'bartenders-td--mono-empty'}`}>
                        {user.mobilePhone ?? '—'}
                      </td>
                      <td className={`bartenders-td ${personalEmail ? 'bartenders-td--small' : 'bartenders-td--small-empty'}`}>
                        {personalEmail ?? '—'}
                      </td>
                      <td className={`bartenders-td ${hireDate ? 'bartenders-td--date' : 'bartenders-td--date-empty'}`}>
                        {hireDate ?? '—'}
                      </td>
                      <td className="bartenders-td bartenders-td--actions">
                        {isConfirming ? (
                          <span className="bartenders-confirm-row">
                            <span className="bartenders-confirm-label">Remove from Entra?</span>
                            <button
                              className="bartenders-btn-danger"
                              onClick={() => handleDelete(user.id)}
                              disabled={deleteMutation.isPending}
                            >
                              {deleteMutation.isPending ? 'Removing…' : 'Yes, remove'}
                            </button>
                            <button
                              className="bartenders-btn-cancel-sm"
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <span className="bartenders-row-actions">
                            <button
                              className="bartenders-remove-btn"
                              title="Edit bartender"
                              onClick={() => setEditUser(user)}
                            >
                              <Icon name="pencil" size={13} />
                            </button>
                            <button
                              className="bartenders-remove-btn"
                              title="Generate new Temporary Access Pass"
                              onClick={() => setTapModalUser({ id: user.id, displayName })}
                            >
                              <Icon name="key" size={13} />
                            </button>
                            <button
                              className="bartenders-remove-btn"
                              onClick={() => setConfirmDeleteId(user.id)}
                            >
                              <Icon name="trash" size={13} /> Remove
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && <CreateBartenderModal onClose={() => setModalOpen(false)} />}
      {editUser && <EditBartenderModal user={editUser} onClose={() => setEditUser(null)} />}
      {tapModalUser && (
        <GenerateTapModal
          userId={tapModalUser.id}
          displayName={tapModalUser.displayName}
          onClose={() => setTapModalUser(null)}
        />
      )}
    </div>
  );
}
